/**
 * StandardPane — atomic structural reference. Every pane (General, Student,
 * Client, Admin) renders the EXACT SAME list of UI aspects in the EXACT
 * SAME order. Copy is structural ("Band Indicator", "This Is Page Header
 * Title", "Bullet item one") so the only thing that changes between panes
 * is the theme — colors, fonts, surface treatments. No marketing copy.
 *
 * Aspects (atomic, one element each):
 *   01 Sidebar
 *   02 Topbar
 *   03 Page header   (eyebrow + headline + subhead)
 *   04 Stat tile
 *   05 Program card
 *   06 Editorial band   (alt-surface, eyebrow + headline ONLY)
 *   07 Bullet list
 *   08 Profile card
 *   09 List row
 *   10 Empty state
 *   11 Button family
 *   12 Pill / badge
 *   13 Form field
 *   14 Banner
 *   15 Modal
 */
import { Text, View } from "react-native";
import Svg, { Circle, Path, Rect } from "react-native-svg";

import type { PaneTheme } from "./paneThemes";
import { StatStrip } from "../aspects/StatStrip";
import { StatusCard } from "../aspects/StatusCard";
import { TimelineRail } from "../aspects/TimelineRail";
import { HeroCard } from "../aspects/HeroCard";
import { TeamCard } from "../aspects/TeamCard";
import { AnnouncementsCard } from "../aspects/AnnouncementsCard";
import { ProgramCard } from "../aspects/ProgramCard";
import { EditorialIntro } from "../aspects/EditorialIntro";
import { DueSoonRail } from "../aspects/DueSoonRail";
import { WorkspaceHero } from "../aspects/WorkspaceHero";
import { SectionHeader } from "../aspects/SectionHeader";
import { AvatarCluster } from "../aspects/AvatarCluster";
import { TextLink } from "../aspects/TextLink";
import { ProgressBar } from "../aspects/ProgressBar";
import { StatusPill } from "../aspects/StatusPill";
import { Donut } from "../aspects/Donut";
import { Kicker } from "../aspects/Kicker";
import { ListRow, ListRowGroup } from "../aspects/ListRow";
import { COLORS } from "../../lib/brand";

const SIDEBAR_W_WIDE = 232;
const SIDEBAR_W_NARROW = 60;

/* ─────── structural labels (intentionally non-marketing) ─────── */
const BAND_INDICATOR = "Band Indicator";
const PAGE_HEADER_TITLE = "This Is Page Header Title";
const PAGE_HEADER_ACCENT = "Page Header";
const EDITORIAL_TITLE = "This Is Editorial Band Title";
const EDITORIAL_ACCENT = "Editorial Band";
const SUBHEAD = "Subhead description sits on this line.";
const BULLETS = ["Bullet item one", "Bullet item two", "Bullet item three"];
const PRIMARY_CTA = "Primary Action";
const SECONDARY_CTA = "Secondary Action";
const DESTRUCTIVE_CTA = "Destructive";
const PROFILE_NAME = "Profile Name";
const PROFILE_ROLE = "Role Label";
const PROFILE_META = "Meta · Detail · Detail";
const PROGRAM_TAG = "Category · Type";
const PROGRAM_TITLE = "Program Card Title";
const PROGRAM_META = "Meta · Meta · Meta";
const LIST_ROWS = [
  { left: "List Row Title", right: "Meta · Detail" },
  { left: "List Row Title", right: "Meta · Detail" },
];
const EMPTY_TITLE = "Empty State Title";
const EMPTY_BODY = "Empty state description sits on this line.";
const FORM_FIELDS = [
  { label: "Field Label", value: "Input value text" },
  { label: "Field Label", value: "Placeholder text", placeholder: true },
];
const BANNER_TITLE = "Banner Title";
const BANNER_BODY = "Banner body copy sits on this line.";
const MODAL_TITLE = "Modal Title";
const MODAL_BODY = "Modal description sits on this line.";

export function StandardPane({
  theme,
  wide,
  pad,
}: {
  theme: PaneTheme;
  wide: boolean;
  pad: number;
}) {
  return (
    <View style={{ backgroundColor: theme.pageBg }}>
      <Topbar theme={theme} wide={wide} pad={pad} />
      <View style={{ flexDirection: "row", minHeight: 600 }}>
        <Sidebar theme={theme} wide={wide} />
        <View
          style={{
            flex: 1,
            backgroundColor: theme.contentBg,
            paddingTop: wide ? 36 : 22,
            paddingBottom: wide ? 48 : 28,
            gap: wide ? 36 : 24,
          }}
        >
          <View style={{ paddingHorizontal: pad, gap: wide ? 36 : 24 }}>
            <PageHeaderAspect theme={theme} wide={wide} number={3} />
            <StatTilesAspect theme={theme} wide={wide} number={4} />
            <ProgramCardAspect theme={theme} wide={wide} number={5} />
          </View>
          <EditorialBandAspect theme={theme} wide={wide} pad={pad} number={6} />
          <View style={{ paddingHorizontal: pad, gap: wide ? 36 : 24 }}>
            <BulletListAspect theme={theme} number={7} />
            <ProfileCardAspect theme={theme} wide={wide} number={8} />
            <ListRowAspect theme={theme} number={9} />
            <EmptyStateAspect theme={theme} number={10} />
            <ButtonFamilyAspect theme={theme} number={11} />
            <PillBadgeAspect theme={theme} number={12} />
            <FormFieldAspect theme={theme} number={13} />
            <BannerAspect theme={theme} number={14} />
            <ModalAspect theme={theme} number={15} />
            <StatStripAspect theme={theme} number={16} />
            <StatusCardAspect theme={theme} number={17} />
            <TimelineRailAspect theme={theme} number={18} />
            <HeroCardAspect theme={theme} number={19} />
            <TeamCardAspect theme={theme} number={20} />
            <AnnouncementsCardAspect theme={theme} number={21} />
            <ProgramCardSharedAspect theme={theme} number={22} />
            <EditorialIntroAspect theme={theme} number={23} />
            <DueSoonRailAspect theme={theme} number={24} />
            <WorkspaceHeroAspect theme={theme} number={25} />
            <SectionHeaderAspect theme={theme} number={26} />
            <AvatarClusterAspect theme={theme} number={27} />
            <TextLinkAspect theme={theme} number={28} />
            <ProgressBarAspect theme={theme} number={29} />
            <StatusPillAspect theme={theme} number={30} />
            <DonutAspect theme={theme} number={31} />
            <KickerAspect theme={theme} number={32} />
          </View>
        </View>
      </View>
    </View>
  );
}

/* ─────────────────────── helpers ─────────────────────── */

function AspectHeading({
  number,
  title,
  theme,
  onAlt,
}: {
  number: number;
  title: string;
  theme: PaneTheme;
  onAlt?: boolean;
}) {
  const dimColor =
    onAlt && theme.altTextOnSurfaceDim === "rgba(28,43,26,0.62)"
      ? "rgba(28,43,26,0.36)"
      : theme.textOnSurfaceFaint;
  return (
    <View style={{ flexDirection: "row", alignItems: "baseline", gap: 8, marginBottom: 14 }}>
      <Text
        style={{
          fontFamily: theme.bodySemiBoldFont,
          fontSize: 10,
          letterSpacing: 2.2,
          textTransform: "uppercase",
          color: theme.accent,
        }}
      >
        Aspect {String(number).padStart(2, "0")}
      </Text>
      <Text
        style={{
          fontFamily: theme.bodyMediumFont,
          fontSize: 10,
          letterSpacing: 2.2,
          textTransform: "uppercase",
          color: dimColor,
        }}
      >
        · {title}
      </Text>
    </View>
  );
}

function renderHeadlineSplit(
  text: string,
  accent: string | undefined,
  theme: PaneTheme,
) {
  if (!accent || !text.includes(accent)) return text;
  const parts = text.split(accent);
  return parts.map((seg, i, arr) => (
    <Text key={i}>
      {seg}
      {i < arr.length - 1 ? (
        <Text
          style={{
            fontFamily: theme.displayItalicFont,
            color: theme.accent,
          }}
        >
          {accent}
        </Text>
      ) : null}
    </Text>
  ));
}

/* ─────────────────────── 01 Sidebar ─────────────────────── */

const NAV_ITEMS: Array<{ label: string; icon: "dash" | "list" | "team" | "cog"; active?: boolean }> = [
  { label: "Nav Item Active", icon: "dash", active: true },
  { label: "Nav Item", icon: "list" },
  { label: "Nav Item", icon: "team" },
  { label: "Nav Item", icon: "cog" },
];

function Sidebar({ theme, wide }: { theme: PaneTheme; wide: boolean }) {
  const width = wide ? SIDEBAR_W_WIDE : SIDEBAR_W_NARROW;
  return (
    <View
      style={{
        width,
        backgroundColor: theme.sidebarBg,
        paddingVertical: 18,
        paddingHorizontal: wide ? 14 : 8,
        gap: 4,
        borderRightWidth: 1,
        borderRightColor: theme.divider,
      }}
    >
      {wide ? (
        <Text
          style={{
            fontFamily: theme.bodySemiBoldFont,
            fontSize: 9,
            letterSpacing: 2.6,
            textTransform: "uppercase",
            color: theme.textOnSidebarDim,
            marginBottom: 12,
            marginLeft: 6,
          }}
        >
          Aspect 01 · Sidebar
        </Text>
      ) : null}
      {NAV_ITEMS.map((it, i) => (
        <View
          key={`${it.label}-${i}`}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 12,
            paddingVertical: 9,
            paddingHorizontal: wide ? 10 : 6,
            borderRadius: 6,
            backgroundColor: it.active ? theme.sidebarItemActiveBg : "transparent",
            justifyContent: wide ? "flex-start" : "center",
          }}
        >
          <NavIcon kind={it.icon} color={it.active ? theme.accent : theme.textOnSidebarDim} />
          {wide ? (
            <Text
              style={{
                fontFamily: theme.bodyMediumFont,
                fontSize: 12.5,
                color: it.active ? theme.textOnSidebar : theme.textOnSidebarDim,
              }}
            >
              {it.label}
            </Text>
          ) : null}
        </View>
      ))}
    </View>
  );
}

function NavIcon({ kind, color }: { kind: string; color: string }) {
  const sw = 1.2;
  switch (kind) {
    case "dash":
      return (
        <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
          <Rect x={1} y={1} width={5} height={5} rx={1} stroke={color} strokeWidth={sw} />
          <Rect x={8} y={1} width={5} height={5} rx={1} stroke={color} strokeWidth={sw} />
          <Rect x={1} y={8} width={5} height={5} rx={1} stroke={color} strokeWidth={sw} />
          <Rect x={8} y={8} width={5} height={5} rx={1} stroke={color} strokeWidth={sw} />
        </Svg>
      );
    case "list":
      return (
        <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
          <Path d="M2 3 H12 M2 7 H12 M2 11 H12" stroke={color} strokeWidth={sw} strokeLinecap="round" />
        </Svg>
      );
    case "team":
      return (
        <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
          <Circle cx={5} cy={5} r={2.2} stroke={color} strokeWidth={sw} />
          <Circle cx={10} cy={6} r={1.7} stroke={color} strokeWidth={sw} />
          <Path d="M1 12 C 1 9.5 9 9.5 9 12" stroke={color} strokeWidth={sw} fill="none" />
        </Svg>
      );
    case "cog":
      return (
        <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
          <Circle cx={7} cy={7} r={2.2} stroke={color} strokeWidth={sw} />
          <Path
            d="M7 1.5 V3 M7 11 V12.5 M1.5 7 H3 M11 7 H12.5"
            stroke={color}
            strokeWidth={sw}
            strokeLinecap="round"
          />
        </Svg>
      );
    case "bell":
      return (
        <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
          <Path d="M3 10 C 3 5, 11 5, 11 10 Z" stroke={color} strokeWidth={sw} fill="none" />
          <Path d="M6 11 C 6.4 12, 7.6 12, 8 11" stroke={color} strokeWidth={sw} fill="none" />
        </Svg>
      );
    default:
      return null;
  }
}

/* ─────────────────────── 02 Topbar ─────────────────────── */

function Topbar({ theme, wide, pad }: { theme: PaneTheme; wide: boolean; pad: number }) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: pad,
        paddingVertical: 14,
        backgroundColor: theme.topbarBg,
        borderBottomWidth: 1,
        borderBottomColor: theme.divider,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 14 }}>
        <Wordmark theme={theme} />
        {wide ? (
          <Text
            style={{
              fontFamily: theme.bodySemiBoldFont,
              fontSize: 9,
              letterSpacing: 2.6,
              textTransform: "uppercase",
              color: theme.textOnTopbarDim,
              marginLeft: 14,
            }}
          >
            Aspect 02 · Topbar
          </Text>
        ) : null}
      </View>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <IconCircle theme={theme} kind="bell" />
        <IconCircle theme={theme} kind="cog" />
        <Avatar theme={theme} initials="AB" />
      </View>
    </View>
  );
}

function Wordmark({ theme }: { theme: PaneTheme }) {
  return (
    <Text
      style={{
        fontFamily: theme.displayMediumFont,
        fontSize: 18,
        letterSpacing: 6,
        color: theme.textOnTopbar,
      }}
    >
      COR
      <Text style={{ color: theme.accent }}>A</Text>
      VU
    </Text>
  );
}

function IconCircle({ theme, kind }: { theme: PaneTheme; kind: "bell" | "cog" }) {
  return (
    <View
      style={{
        width: 30,
        height: 30,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: theme.divider,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <NavIcon kind={kind} color={theme.textOnTopbarDim} />
    </View>
  );
}

function Avatar({ theme, initials }: { theme: PaneTheme; initials: string }) {
  return (
    <View
      style={{
        width: 32,
        height: 32,
        borderRadius: 999,
        backgroundColor: theme.accent,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Text
        style={{
          fontFamily: theme.bodyBoldFont,
          fontSize: 11,
          letterSpacing: 1.2,
          color: theme.accentText,
        }}
      >
        {initials}
      </Text>
    </View>
  );
}

/* ─────────────────────── 03 Page header ─────────────────────── */

function PageHeaderAspect({ theme, wide, number }: { theme: PaneTheme; wide: boolean; number: number }) {
  const size = wide ? 42 : 30;
  return (
    <View>
      <AspectHeading number={number} title="Page header" theme={theme} />
      <View style={{ flexDirection: "row", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <View style={{ width: 22, height: 1, backgroundColor: theme.accent }} />
        <Text
          style={{
            fontFamily: theme.bodySemiBoldFont,
            fontSize: 10.5,
            letterSpacing: 3.2,
            textTransform: "uppercase",
            color: theme.accent,
          }}
        >
          {BAND_INDICATOR}
        </Text>
      </View>
      <Text
        style={{
          fontFamily: theme.displayMediumFont,
          fontSize: size,
          lineHeight: size * 1.08,
          color: theme.textOnSurface,
          letterSpacing: -0.4,
          marginBottom: 12,
        }}
      >
        {renderHeadlineSplit(PAGE_HEADER_TITLE, PAGE_HEADER_ACCENT, theme)}
      </Text>
      <Text
        style={{
          fontFamily: theme.bodyFont,
          fontSize: 14,
          lineHeight: 22,
          color: theme.textOnSurfaceDim,
          maxWidth: 540,
        }}
      >
        {SUBHEAD}
      </Text>
    </View>
  );
}

/* ─────────────────────── 04 Stat tile ─────────────────────── */

function StatTilesAspect({ theme, wide, number }: { theme: PaneTheme; wide: boolean; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Stat tile" theme={theme} />
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
        {[1, 2, 3].map((i) => (
          <View
            key={i}
            style={{
              flex: 1,
              minWidth: wide ? 140 : 150,
              padding: 18,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: theme.border,
              backgroundColor: theme.surfaceBg,
            }}
          >
            <Text
              style={{
                fontFamily: theme.bodySemiBoldFont,
                fontSize: 10,
                letterSpacing: 1.8,
                textTransform: "uppercase",
                color: theme.textOnSurfaceFaint,
                marginBottom: 10,
              }}
            >
              Stat Label
            </Text>
            <Text
              style={{
                fontFamily: theme.displayFont,
                fontSize: 34,
                lineHeight: 36,
                color: theme.textOnSurface,
                letterSpacing: -0.6,
              }}
            >
              00
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}

/* ─────────────────────── 05 Program card ─────────────────────── */

function ProgramCardAspect({ theme, wide, number }: { theme: PaneTheme; wide: boolean; number: number }) {
  const cs = theme.cardStyle;
  return (
    <View>
      <AspectHeading number={number} title="Program card" theme={theme} />
      <View
        style={{
          backgroundColor: cs.bg,
          borderRadius: 8,
          borderWidth: 1,
          borderColor: cs.borderColor,
          borderLeftWidth: cs.borderLeftAccent ? 3 : 1,
          borderLeftColor: cs.borderLeftAccent ? theme.accent : cs.borderColor,
          padding: 20,
          gap: 8,
          maxWidth: wide ? 460 : undefined,
        }}
      >
        <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
          <Text
            style={{
              fontFamily: theme.bodyBoldFont,
              fontSize: 9.5,
              letterSpacing: 2.6,
              textTransform: "uppercase",
              color: cs.tagOnCard,
            }}
          >
            {PROGRAM_TAG}
          </Text>
          <View
            style={{
              paddingHorizontal: 8,
              paddingVertical: 2,
              borderRadius: 999,
              borderWidth: 1,
              borderColor: theme.success,
            }}
          >
            <Text
              style={{
                fontFamily: theme.bodyBoldFont,
                fontSize: 9,
                letterSpacing: 1.4,
                color: theme.success,
              }}
            >
              STATUS
            </Text>
          </View>
        </View>
        <Text
          style={{
            fontFamily: theme.displayMediumFont,
            fontSize: 20,
            lineHeight: 24,
            color: cs.textOnCard,
          }}
        >
          {PROGRAM_TITLE}
        </Text>
        <Text
          style={{
            fontFamily: theme.bodyFont,
            fontSize: 11.5,
            color: cs.textDimOnCard,
          }}
        >
          {PROGRAM_META}
        </Text>
      </View>
    </View>
  );
}

/* ─────────────────────── 06 Editorial band ─────────────────────── */

function EditorialBandAspect({
  theme,
  wide,
  pad,
  number,
}: {
  theme: PaneTheme;
  wide: boolean;
  pad: number;
  number: number;
}) {
  const size = wide ? 42 : 30;
  return (
    <View
      style={{
        backgroundColor: theme.altBg,
        paddingHorizontal: pad,
        paddingVertical: wide ? 52 : 32,
      }}
    >
      <AspectHeading number={number} title={theme.altSurfaceLabel} theme={theme} onAlt />
      <View style={{ flexDirection: "row", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <View style={{ width: 22, height: 1, backgroundColor: theme.accent }} />
        <Text
          style={{
            fontFamily: theme.bodySemiBoldFont,
            fontSize: 10.5,
            letterSpacing: 3.2,
            textTransform: "uppercase",
            color: theme.accent,
          }}
        >
          {BAND_INDICATOR}
        </Text>
      </View>
      <Text
        style={{
          fontFamily: theme.displayMediumFont,
          fontSize: size,
          lineHeight: size * 1.08,
          color: theme.altTextOnSurface,
          letterSpacing: -0.4,
          maxWidth: 720,
        }}
      >
        {renderHeadlineSplit(EDITORIAL_TITLE, EDITORIAL_ACCENT, theme)}
      </Text>
    </View>
  );
}

/* ─────────────────────── 07 Bullet list ─────────────────────── */

function BulletListAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Bullet list" theme={theme} />
      <View style={{ gap: 14 }}>
        {BULLETS.map((b) => (
          <View key={b} style={{ flexDirection: "row", alignItems: "center", gap: 14 }}>
            <View
              style={{
                width: 20,
                height: 20,
                borderRadius: 999,
                borderWidth: 1,
                borderColor: theme.accent,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <View
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 999,
                  backgroundColor: theme.accent,
                }}
              />
            </View>
            <Text
              style={{
                fontFamily: theme.displayMediumFont,
                fontSize: 19,
                lineHeight: 26,
                color: theme.textOnSurface,
                flex: 1,
              }}
            >
              {b}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}

/* ─────────────────────── 08 Profile card ─────────────────────── */

function ProfileCardAspect({ theme, wide, number }: { theme: PaneTheme; wide: boolean; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Profile card" theme={theme} />
      <View
        style={{
          flexDirection: wide ? "row" : "column",
          gap: 20,
          backgroundColor: theme.surfaceBg,
          borderWidth: 1,
          borderColor: theme.border,
          borderRadius: 10,
          padding: 20,
          alignItems: wide ? "center" : "flex-start",
          maxWidth: wide ? 560 : undefined,
        }}
      >
        <View
          style={{
            width: 64,
            height: 64,
            borderRadius: 999,
            backgroundColor: theme.accentSoft,
            borderWidth: 1,
            borderColor: theme.borderStrong,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text
            style={{
              fontFamily: theme.displayMediumFont,
              fontSize: 22,
              color: theme.accent,
            }}
          >
            PN
          </Text>
        </View>
        <View style={{ flex: 1, gap: 4 }}>
          <Text
            style={{
              fontFamily: theme.bodySemiBoldFont,
              fontSize: 10,
              letterSpacing: 2.2,
              textTransform: "uppercase",
              color: theme.accent,
            }}
          >
            {PROFILE_ROLE}
          </Text>
          <Text
            style={{
              fontFamily: theme.displayMediumFont,
              fontSize: 22,
              color: theme.textOnSurface,
            }}
          >
            {PROFILE_NAME}
          </Text>
          <Text
            style={{
              fontFamily: theme.bodyFont,
              fontSize: 12.5,
              color: theme.textOnSurfaceDim,
            }}
          >
            {PROFILE_META}
          </Text>
        </View>
      </View>
    </View>
  );
}

/* ─────────────────────── 09 List row ─────────────────────── */

function ListRowAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="List row" theme={theme} />
      <ListRowGroup theme={theme}>
        {LIST_ROWS.map((r, i) => (
          <ListRow
            key={i}
            theme={theme}
            first={i === 0}
            title={r.left}
            meta={r.right}
          />
        ))}
      </ListRowGroup>
    </View>
  );
}

/* ─────────────────────── 10 Empty state ─────────────────────── */

function EmptyStateAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Empty state" theme={theme} />
      <View
        style={{
          backgroundColor: theme.surfaceBg,
          borderWidth: 1,
          borderColor: theme.border,
          borderRadius: 8,
          padding: 28,
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        }}
      >
        <Text
          style={{
            fontFamily: theme.bodySemiBoldFont,
            fontSize: 10,
            letterSpacing: 2.2,
            textTransform: "uppercase",
            color: theme.accent,
          }}
        >
          {BAND_INDICATOR}
        </Text>
        <Text
          style={{
            fontFamily: theme.displayMediumFont,
            fontSize: 22,
            color: theme.textOnSurface,
            textAlign: "center",
          }}
        >
          {EMPTY_TITLE}
        </Text>
        <Text
          style={{
            fontFamily: theme.bodyFont,
            fontSize: 12.5,
            color: theme.textOnSurfaceDim,
            textAlign: "center",
            maxWidth: 320,
            marginBottom: 6,
          }}
        >
          {EMPTY_BODY}
        </Text>
        <FilledButton theme={theme} label={PRIMARY_CTA} />
      </View>
    </View>
  );
}

/* ─────────────────────── 11 Button family ─────────────────────── */

function ButtonFamilyAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Button family" theme={theme} />
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
        <FilledButton theme={theme} label={PRIMARY_CTA} />
        <OutlineButton theme={theme} label={SECONDARY_CTA} />
        <DestructiveButton theme={theme} label={DESTRUCTIVE_CTA} />
      </View>
    </View>
  );
}

function FilledButton({ theme, label }: { theme: PaneTheme; label: string }) {
  return (
    <View
      style={{
        backgroundColor: theme.accent,
        borderRadius: 6,
        paddingHorizontal: 14,
        paddingVertical: 10,
      }}
    >
      <Text
        style={{
          fontFamily: theme.bodyBoldFont,
          fontSize: 11.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: theme.accentText,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

function OutlineButton({ theme, label }: { theme: PaneTheme; label: string }) {
  return (
    <View
      style={{
        borderRadius: 6,
        borderWidth: 1,
        borderColor: theme.borderStrong,
        paddingHorizontal: 14,
        paddingVertical: 10,
      }}
    >
      <Text
        style={{
          fontFamily: theme.bodyBoldFont,
          fontSize: 11.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: theme.accent,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

function GhostButton({ theme, label }: { theme: PaneTheme; label: string }) {
  return (
    <View style={{ paddingHorizontal: 12, paddingVertical: 10 }}>
      <Text
        style={{
          fontFamily: theme.bodyMediumFont,
          fontSize: 11.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: theme.textOnSurfaceDim,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

function DestructiveButton({ theme, label }: { theme: PaneTheme; label: string }) {
  return (
    <View
      style={{
        borderRadius: 6,
        borderWidth: 1,
        borderColor: theme.destructive,
        paddingHorizontal: 14,
        paddingVertical: 10,
      }}
    >
      <Text
        style={{
          fontFamily: theme.bodyBoldFont,
          fontSize: 11.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: theme.destructive,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

/* ─────────────────────── 12 Pill / badge ─────────────────────── */

function PillBadgeAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Pill / badge" theme={theme} />
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
        <Pill theme={theme} label="STATUS" tone="success" />
        <Pill theme={theme} label="STATUS" tone="accent" />
        <Pill theme={theme} label="STATUS" tone="neutral" />
      </View>
    </View>
  );
}

function Pill({
  theme,
  label,
  tone,
}: {
  theme: PaneTheme;
  label: string;
  tone: "success" | "accent" | "neutral";
}) {
  const color =
    tone === "success" ? theme.success : tone === "accent" ? theme.accent : theme.textOnSurfaceDim;
  return (
    <View
      style={{
        paddingHorizontal: 10,
        paddingVertical: 3,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: color,
      }}
    >
      <Text
        style={{
          fontFamily: theme.bodyBoldFont,
          fontSize: 9.5,
          letterSpacing: 1.6,
          color,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

/* ─────────────────────── 13 Form field ─────────────────────── */

function FormFieldAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Form field" theme={theme} />
      <View
        style={{
          backgroundColor: theme.surfaceBg,
          borderWidth: 1,
          borderColor: theme.border,
          borderRadius: 10,
          padding: 20,
          gap: 14,
          maxWidth: 520,
        }}
      >
        {FORM_FIELDS.map((f, i) => (
          <View key={i} style={{ gap: 6 }}>
            <Text
              style={{
                fontFamily: theme.bodySemiBoldFont,
                fontSize: 10,
                letterSpacing: 2.2,
                textTransform: "uppercase",
                color: theme.textOnSurfaceFaint,
              }}
            >
              {f.label}
            </Text>
            <View
              style={{
                borderWidth: 1,
                borderColor: theme.borderStrong,
                borderRadius: 6,
                paddingHorizontal: 12,
                paddingVertical: 10,
                backgroundColor: theme.surfaceRaisedBg,
              }}
            >
              <Text
                style={{
                  fontFamily: theme.bodyFont,
                  fontSize: 13,
                  color: f.placeholder ? theme.textOnSurfaceFaint : theme.textOnSurface,
                }}
              >
                {f.value}
              </Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}

/* ─────────────────────── 14 Banner ─────────────────────── */

function BannerAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Banner" theme={theme} />
      <View style={{ gap: 10 }}>
        <Banner theme={theme} tone="success" />
        <Banner theme={theme} tone="error" />
        <Banner theme={theme} tone="info" />
      </View>
    </View>
  );
}

function Banner({
  theme,
  tone,
}: {
  theme: PaneTheme;
  tone: "success" | "error" | "info";
}) {
  const color = tone === "success" ? theme.success : tone === "error" ? theme.destructive : theme.accent;
  return (
    <View
      style={{
        flexDirection: "row",
        gap: 12,
        backgroundColor: theme.surfaceBg,
        borderWidth: 1,
        borderColor: theme.border,
        borderLeftWidth: 3,
        borderLeftColor: color,
        borderRadius: 8,
        paddingVertical: 14,
        paddingHorizontal: 16,
      }}
    >
      <View
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          backgroundColor: color,
          marginTop: 6,
        }}
      />
      <View style={{ flex: 1, gap: 4 }}>
        <Text
          style={{
            fontFamily: theme.bodySemiBoldFont,
            fontSize: 12.5,
            color: theme.textOnSurface,
          }}
        >
          {BANNER_TITLE}
        </Text>
        <Text
          style={{
            fontFamily: theme.bodyFont,
            fontSize: 12,
            lineHeight: 18,
            color: theme.textOnSurfaceDim,
          }}
        >
          {BANNER_BODY}
        </Text>
      </View>
    </View>
  );
}

/* ─────────────────────── 15 Modal ─────────────────────── */

function ModalAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Modal" theme={theme} />
      <View
        style={{
          backgroundColor: "rgba(0,0,0,0.45)",
          borderRadius: 10,
          padding: 22,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <View
          style={{
            width: "100%",
            maxWidth: 420,
            backgroundColor: theme.surfaceRaisedBg,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: theme.borderStrong,
            padding: 20,
            gap: 12,
          }}
        >
          <Text
            style={{
              fontFamily: theme.bodySemiBoldFont,
              fontSize: 10,
              letterSpacing: 2.4,
              textTransform: "uppercase",
              color: theme.accent,
            }}
          >
            {BAND_INDICATOR}
          </Text>
          <Text
            style={{
              fontFamily: theme.displayMediumFont,
              fontSize: 22,
              color: theme.textOnSurface,
            }}
          >
            {MODAL_TITLE}
          </Text>
          <Text
            style={{
              fontFamily: theme.bodyFont,
              fontSize: 13,
              lineHeight: 20,
              color: theme.textOnSurfaceDim,
            }}
          >
            {MODAL_BODY}
          </Text>
          <View style={{ flexDirection: "row", gap: 8, justifyContent: "flex-end", marginTop: 6 }}>
            <GhostButton theme={theme} label="Cancel" />
            <DestructiveButton theme={theme} label={DESTRUCTIVE_CTA} />
          </View>
        </View>
      </View>
    </View>
  );
}


/* ─────────────────────── 16 Stat strip ─────────────────────── */

function StatStripAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Stat strip" theme={theme} />
      <StatStrip
        theme={theme}
        eyebrow="Stat Strip"
        cells={[
          { label: "Label", value: "00" },
          { label: "Label", value: "00" },
          { label: "Label", value: "00" },
          { label: "Label", value: "00" },
        ]}
      />
    </View>
  );
}


/* ─────────────────────── 17 Status card ─────────────────────── */

function StatusCardAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Status card" theme={theme} />
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 12 }}>
        <View style={{ flexBasis: "48%", flexGrow: 1, minWidth: 220 }}>
          <StatusCard
            theme={theme}
            tone="accent"
            chipLabel="In Review"
            dateLabel="May 22"
            title="Status Card Title"
            progress={0.6}
          />
        </View>
        <View style={{ flexBasis: "48%", flexGrow: 1, minWidth: 220 }}>
          <StatusCard
            theme={theme}
            tone="success"
            chipLabel="Active"
            dateLabel="Jun 5"
            title="Status Card Title"
            progress={0.35}
          />
        </View>
      </View>
    </View>
  );
}


/* ─────────────────────── 18 Timeline rail ─────────────────────── */

function TimelineRailAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Timeline rail" theme={theme} />
      <TimelineRail
        theme={theme}
        items={[
          { key: "a", label: "Milestone Complete", dateLabel: "May 8", status: "complete", note: "Passed" },
          { key: "b", label: "Milestone Active",   dateLabel: "May 22", status: "active",   note: "In progress · 5d left" },
          { key: "c", label: "Milestone Upcoming", dateLabel: "Jun 8",  status: "upcoming" },
        ]}
      />
    </View>
  );
}


/* ─────────────────────── 19 Hero card ─────────────────────── */

function HeroCardAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Hero card" theme={theme} />
      <HeroCard
        theme={theme}
        overline="Up next"
        title="Hero Card Title"
        subtitle="Subtitle copy sits on this line and supports two-line wrap."
        ctaLabel="Open Submissions"
        onPress={() => {}}
        countdown={{ days: 5, urgent: false }}
      />
    </View>
  );
}


/* ─────────────────────── 20 Team card ─────────────────────── */

function TeamCardAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Team card" theme={theme} />
      <View style={{ flexDirection: "row", gap: 12, flexWrap: "wrap" }}>
        <View style={{ flexBasis: "48%", flexGrow: 1, minWidth: 220 }}>
          <TeamCard
            theme={theme}
            members={[]}
            emptyTitle="Not on a team yet"
            emptySubtitle="Find a public team or build your own."
            ctaLabel="FIND A TEAM"
            onPress={() => {}}
          />
        </View>
        <View style={{ flexBasis: "48%", flexGrow: 1, minWidth: 220 }}>
          <TeamCard
            theme={theme}
            teamName="Team Name"
            isCaptain={true}
            members={[
              { id: "a", name: "Alex Adams" },
              { id: "b", name: "Bri Brown" },
              { id: "c", name: "Cam Chen" },
            ]}
            maxTeamSize={4}
            standing={{ label: "STANDING", value: "#1 of 12 · 81.2 pts", tone: "amber" }}
            ctaLabel="Open team chat"
            onPress={() => {}}
          />
        </View>
      </View>
    </View>
  );
}


/* ─────────────────────── 21 Announcements card ─────────────────────── */

function AnnouncementsCardAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Announcements card" theme={theme} />
      <View style={{ maxWidth: 360 }}>
        <AnnouncementsCard
          theme={theme}
          unreadCount={1}
          items={[
            { id: "a", dateLabel: "Today · 3:42 PM", title: "Announcement headline one", viewed: false },
            { id: "b", dateLabel: "Yesterday", title: "Announcement headline two (already viewed)", viewed: true },
          ]}
          onPressViewAll={() => {}}
        />
      </View>
    </View>
  );
}


/* ─────────────────────── 22 Program card ─────────────────────── */

function ProgramCardSharedAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Program card" theme={theme} />
      <View style={{ flexDirection: "row", gap: 16, flexWrap: "wrap" }}>
        <View style={{ flexBasis: "48%", flexGrow: 1, minWidth: 280 }}>
          <ProgramCard
            theme={theme}
            status="active"
            typeMeta="Open Enrollment · Case Competition"
            title="Program Card Title"
            sponsorName="Sponsor Name"
            phase={{ index: 1, total: 5, name: "Discovery & framing" }}
            daysLeft={12}
            closesDateShort="May 24"
            onPress={() => {}}
          />
        </View>
        <View style={{ flexBasis: "48%", flexGrow: 1, minWidth: 280 }}>
          <ProgramCard
            theme={theme}
            status="judging"
            typeMeta="Open Enrollment · Case Competition"
            title="Program Card Title"
            phase={{ index: 4, total: 5, name: "Final review" }}
            closesDateShort="Jun 8"
            onPress={() => {}}
          />
        </View>
      </View>
    </View>
  );
}


/* ─────────────────────── 23 Editorial intro ─────────────────────── */

function EditorialIntroAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Editorial intro" theme={theme} />
      <EditorialIntro
        theme={theme}
        kicker="Eyebrow Kicker"
        headline={{ before: "Greeting, ", accent: "Italic Accent", after: "." }}
        subtitle="Subtitle copy sits on this line and can wrap to several lines as the editorial voice requires."
        cta={{ label: "Primary Action", onPress: () => {} }}
      />
    </View>
  );
}


/* ─────────────────────── 24 Due soon rail ─────────────────────── */

function DueSoonRailAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Due soon rail" theme={theme} />
      <View style={{ maxWidth: 360 }}>
        <DueSoonRail
          theme={theme}
          totalCount={3}
          items={[
            { key: "a", daysUntil: 1, primaryText: "Urgent Item", metaText: "Comp Name · May 24 · 1 day left" },
            { key: "b", daysUntil: 5, primaryText: "Soon Item", metaText: "Comp Name · May 28 · 5 days left" },
            { key: "c", daysUntil: 10, primaryText: "Distant Item", metaText: "Comp Name · Jun 2 · 10 days left" },
          ]}
          onPressViewAll={() => {}}
        />
      </View>
    </View>
  );
}


/* ─────────────────────── 25 Workspace hero ─────────────────────── */

function WorkspaceHeroAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Workspace hero" theme={theme} />
      <WorkspaceHero
        theme={theme}
        kicker="COMPANY · 2026"
        title="Workspace Hero Title"
        chip={{ label: "Active", tone: "sage" }}
        meta={[
          { icon: "users", label: "Teams of 4" },
          { icon: "calendar", label: "Closes May 24" },
        ]}
        progressPct={0.33}
        progressCaption="1 of 3 deliverables done"
        nextDueLabel="Next due May 22"
      />
    </View>
  );
}


/* ─────────────────────── 26 Section header ─────────────────────── */

function SectionHeaderAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Section header" theme={theme} />
      <View style={{ gap: 18 }}>
        <SectionHeader
          theme={theme}
          title="Section Title"
          count={3}
          subtitle="Subtitle · Right rail"
          tone="light"
        />
        <View style={{ backgroundColor: theme.sidebarBg, padding: 18, borderRadius: 8 }}>
          <SectionHeader
            theme={theme}
            title="Section Title"
            count={3}
            subtitle="Subtitle · Right rail"
            tone="dark"
          />
        </View>
      </View>
    </View>
  );
}


/* ─────────────────────── 27 Avatar cluster ─────────────────────── */

function AvatarClusterAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Avatar cluster" theme={theme} />
      <View style={{ gap: 20, padding: 18, backgroundColor: theme.surfaceRaisedBg, borderRadius: 8, borderWidth: 1, borderColor: theme.border, maxWidth: 360 }}>
        <AvatarCluster
          theme={theme}
          members={[
            { id: "a", name: "Alex Adams" },
            { id: "b", name: "Bri Brown" },
            { id: "c", name: "Cam Chen" },
          ]}
          countMeta="3 of 4"
        />
        <AvatarCluster
          theme={theme}
          members={[
            { id: "a", name: "Alex Adams" },
            { id: "b", name: "Bri Brown" },
            { id: "c", name: "Cam Chen" },
            { id: "d", name: "Dee Davis" },
            { id: "e", name: "Eli Evans" },
            { id: "f", name: "Fay Foster" },
          ]}
          maxVisible={4}
          countMeta="6 of 8"
        />
      </View>
    </View>
  );
}


/* ─────────────────────── 28 Text link ─────────────────────── */

function TextLinkAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Text link" theme={theme} />
      <View style={{ gap: 12 }}>
        <TextLink theme={theme} label="View all" onPress={() => {}} tone="accent" />
        <TextLink theme={theme} label="Open team chat" onPress={() => {}} tone="accentDeep" />
        <TextLink theme={theme} label="Find a team" onPress={() => {}} tone="accent" showArrow={false} />
      </View>
    </View>
  );
}


/* ─────────────────────── 29 Progress bar ─────────────────────── */

function ProgressBarAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Progress bar" theme={theme} />
      <View style={{ gap: 14, maxWidth: 420 }}>
        <ProgressBar theme={theme} segments={5} current={1} />
        <ProgressBar theme={theme} segments={5} current={3} />
        <ProgressBar theme={theme} segments={5} current={5} />
      </View>
    </View>
  );
}


/* ─────────────────────── 30 Status pill ─────────────────────── */

function StatusPillAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Status pill" theme={theme} />
      <View style={{ flexDirection: "row", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <StatusPill theme={theme} label="Active" tone="sage" />
        <StatusPill theme={theme} label="In Review" tone="amber" />
        <StatusPill theme={theme} label="Closed" tone="grey" />
        <StatusPill theme={theme} label="Draft" tone="grey" showDot={false} />
      </View>
    </View>
  );
}


/* ─────────────────────── 31 Donut ─────────────────────── */

function DonutAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Donut" theme={theme} />
      <View
        style={{
          flexDirection: "row",
          gap: 22,
          padding: 22,
          backgroundColor: theme.sidebarBg,
          borderRadius: 8,
        }}
      >
        <Donut theme={theme} pct={0} labelColor={COLORS.LINEN.hex} />
        <Donut theme={theme} pct={0.33} labelColor={COLORS.LINEN.hex} />
        <Donut theme={theme} pct={1} labelColor={COLORS.LINEN.hex} />
      </View>
    </View>
  );
}


/* ─────────────────────── 32 Kicker ─────────────────────── */

function KickerAspect({ theme, number }: { theme: PaneTheme; number: number }) {
  return (
    <View>
      <AspectHeading number={number} title="Kicker" theme={theme} />
      <View style={{ gap: 14 }}>
        <Kicker theme={theme} label="Coravu · Student" />
        <Kicker theme={theme} label="Get Started" showHairline={false} />
        <Kicker theme={theme} label="Up Next" hairlineWidth={48} />
      </View>
    </View>
  );
}
